/* eslint-disable max-lines */
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { flatMap, isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, skip, switchMap, tap } from 'rxjs/operators';

import { ButtonComponent } from '@shared/components/button/button.component';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import {
  Currency,
  InvestigatorForecastSource,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Utils } from '@shared/utils/utils';

import { InvestigatorForecastSavedSourceBannerComponent } from './components/investigator-forecast-saved-source-banner/investigator-forecast-saved-source-banner.component';
import { InvestigatorForecastTableComponent } from './components/investigator-forecast-table/investigator-forecast-table.component';
import { InvestigatorForecastUpdateModalComponent } from './components/investigator-forecast-update-modal/investigator-forecast-update-modal.component';
import { InvestigatorForecastOverviewComponent } from './components/investigator-source-select/investigator-overview/investigator-overview.component';
import { InvestigatorForecastSourceSelectComponent } from './components/investigator-source-select/investigator-source-select.component';
import {
  AVERAGES_SOURCE_INITIAL_VALUES,
  CONTRACTED_SOURCE_INITIAL_VALUE,
  INITIAL_FORM_VALUES,
  PATIENT_VISITS_SOURCE_INITIAL_VALUE,
} from './constants/investigator-forecast.const';
import {
  InvestigatorAveragesService,
  InvestigatorForecastLsService,
  InvestigatorForecastService,
} from './services';
import { InvestigatorPatientVisits } from './services/investigator-patient-visits.service';
import {
  ContractedInvestigatorAmountValues,
  InvestigatorForecastForm,
  InvestigatorForecastValues,
  PatientVisitsValues,
  SiteContractAveragesValues,
  Source,
  SourceType,
} from './types/investigator-forecast.types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VarDirective,
    FormsModule,
    ReactiveFormsModule,
    InvestigatorForecastSavedSourceBannerComponent,
    InvestigatorForecastTableComponent,
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    ButtonComponent,
    TooltipDirective,
    InvestigatorForecastSourceSelectComponent,
    InvestigatorForecastOverviewComponent,
    AsyncPipe,
  ],
  selector: 'aux-investigator-forecast',
  styles: [],
  templateUrl: './investigator-forecast.component.html',
})
export class InvestigatorForecastComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly investigatorAveragesService = inject(InvestigatorAveragesService);
  private readonly investigatorForecastLsService = inject(InvestigatorForecastLsService);
  private readonly investigatorForecastService = inject(InvestigatorForecastService);
  private readonly investigatorPatientVisitsService = inject(InvestigatorPatientVisits);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);
  private readonly router = inject(Router);
  private workflowQuery = inject(WorkflowQuery);

  flags = injectFlags({
    showEnrolledPatientsCostsTable: 'investigator_forecast_enrolled_patient_costs_table',
  });

  editButtonTooltipErrors = computed(() => {
    const errors: string[] = [];

    if (!this.hasEditInvestigatorForecastPermission()) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isChangeOrdersWorkflowLocked()) {
      errors.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    return errors;
  });

  bannerValues$ = new BehaviorSubject<{ amount: number; source: string }>({
    amount: 0,
    source: '',
  });
  contractedSourceUpdatedDate: null | string = null;
  defaultSource = SourceType.Contracted;
  doNotHavePermissionMessage = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  editMode$ = new BehaviorSubject(false);
  form: FormGroup<InvestigatorForecastForm> = new FormGroup({
    averages: this.fb.nonNullable.group<SiteContractAveragesValues>(AVERAGES_SOURCE_INITIAL_VALUES),
    contracted: this.fb.nonNullable.group<ContractedInvestigatorAmountValues>(
      CONTRACTED_SOURCE_INITIAL_VALUE
    ),
    patientVisits: this.fb.nonNullable.group<PatientVisitsValues>(
      PATIENT_VISITS_SOURCE_INITIAL_VALUE
    ),
    source: new FormControl<null | SourceType>(null),
    vendorId: new FormControl(''),
  });
  hasEditInvestigatorForecastPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVESTIGATOR_FORECAST],
  });
  initialFormValues?: InvestigatorForecastValues;
  isChangeOrdersWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CHANGE_ORDERS
  );
  patientVisitsSourceUpdatedDate: null | string = null;
  selectedVendorId = '';
  sources: Source[] = [
    {
      label: 'Contracted Investigator Amount',
      value: SourceType.Contracted,
    },
    {
      label: 'Site Contract Averages',
      value: SourceType.Averages,
    },
    {
      icon: 'AuxLogo',
      label: 'Actual Patient Visits',
      value: SourceType.PatientVisits,
    },
  ];
  submitButtonDisabled$ = new BehaviorSubject(false);
  triggerFetchActualDataAfterSubmit$ = new BehaviorSubject(false);
  vendorList: { currency: Currency; id: string; name: string }[] = [];
  vendorListLoading$ = new BehaviorSubject(true);
  viewForecastButtonClicked$ = new BehaviorSubject<boolean>(false);

  private editableControlNames = [
    ['averages', 'contractedSites'],
    ['averages', 'totalForecastedPatients'],
    ['averages', 'averagePatientCost'],
    ['averages', 'invoiceables'],
    ['averages', 'otherInvoiceablesPerc'],
    ['averages', 'overheadInvoiceablesPerc'],
    ['patientVisits', 'invoiceablesPerc'],
    ['patientVisits', 'invoiceablesDistribution'],
    ['patientVisits', 'totalDiscontinuedPerc'],
    ['patientVisits', 'remainingPatientsToDiscontinuePerc'],
  ];
  private mapSaveSiteContractBySource = new Map<SourceType, () => Promise<void>>([
    [SourceType.Averages, () => this.updateAverages()],
    [SourceType.Contracted, () => this.updateSiteContract()],
    [SourceType.PatientVisits, () => this.updatePatientVisits()],
  ]);

  ngOnInit(): void {
    this.fetchVendorList();
    this.updateSaveSettingAvailabilityState();

    const vendorControl = this.form.get('vendorId');
    const sourceControl = this.form.get('source');

    if (!vendorControl || !sourceControl) {
      return;
    }

    this.setControlStateByCondition(sourceControl, !!vendorControl?.value);

    this.viewForecastButtonClicked$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((click) => {
      if (click) {
        this.navigateToBudgetForecast(this.selectedVendorId);
      }
    });

    this.fetchInvestigatorData(vendorControl, sourceControl);

    this.editMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((editMode) => {
      this.investigatorAveragesService.editMode$.next(editMode);
      this.investigatorPatientVisitsService.editMode$.next(editMode);
      this.setEnableStateForEditableControls(editMode);
    });

    this.mainQuery
      .select('trialKey')
      .pipe(takeUntilDestroyed(this.destroyRef), skip(1)) // skip first call on initial fetching data
      .subscribe(() => {
        this.updateBanner(null);
        this.initialFormValues = undefined;
        this.resetPageState();
      });
  }

  private assignBottomValuesToSourceOptions() {
    this.sources = this.sources.map((source) => {
      const values = this.form.getRawValue()[source.value] || {};

      return {
        ...source,
        avgPatientCost: values.avgPatientCost || 0,
        forecastTotal: values.totalInvestigatorCosts || 0,
        remaining: values.remainingAmount || 0,
        spendToDate: values.spendToDateAmount || 0,
      };
    });

    this.cdr.markForCheck();
  }

  cancelEdit = () => {
    this.resetPageState();
  };

  canDeactivate = async (): Promise<boolean> => {
    const { averages, patientVisits, vendorId } = this.form.getRawValue();

    if (!this.initialFormValues || !vendorId) {
      return true;
    }

    const hasChanges = !isEqual(
      {
        averages: this.initialFormValues?.averages,
        patientVisits: this.initialFormValues?.patientVisits,
      },
      {
        averages,
        patientVisits,
      }
    );

    if (hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  };

  toggleEditMode = (mode: boolean): void => {
    this.editMode$.next(mode);
  };

  editModeEnable = () => () => {
    this.toggleEditMode(true);
  };

  fetchInvestigatorData(vendorControl: AbstractControl, sourceControl: AbstractControl) {
    combineLatest([vendorControl.valueChanges, this.triggerFetchActualDataAfterSubmit$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(([vendorId]) => {
          this.selectedVendorId = vendorId || '';

          if (sourceControl && vendorId) {
            this.setControlStateByCondition(sourceControl, true);
          }
        }),
        filter(([vendorId]) => !!vendorId),
        switchMap(() => {
          // Angular form cant update disabled fields
          this.setEnableStateForEditableControls(true);
          this.investigatorForecastService.isLoadingSettings$.next(true);

          return this.investigatorForecastService.getInvestigatorForecastContractedAmounts(
            vendorControl.value || ''
          );
        }),
        tap((contractedValues) => {
          this.contractedSourceUpdatedDate = contractedValues.sourceLastUpdate;

          this.form.get('contracted')?.setValue(contractedValues.values);
        }),
        switchMap((contractedValues) => {
          return this.investigatorForecastService.getInvestigatorForecastPatientVisitsAmounts(
            vendorControl.value || '',
            contractedValues.values.spendToDateAmount
          );
        }),
        tap((patientVisits) => {
          this.patientVisitsSourceUpdatedDate = patientVisits.sourceLastUpdate;

          this.form.get('patientVisits')?.setValue(patientVisits.values);
        }),
        switchMap(() => {
          const vendorId = vendorControl.value;

          return this.investigatorForecastService.getInvestigatorForecastAveragesAmounts(
            vendorId as string,
            this.form.get('contracted')?.getRawValue()
          );
        })
      )
      .subscribe((averagesValues) => {
        this.form.get('averages')?.setValue(averagesValues.values);

        this.investigatorForecastService.isLoadingSettings$.next(false);

        this.assignBottomValuesToSourceOptions();

        if (this.vendorList.length) {
          const source = this.getLastSavedSource(
            this.contractedSourceUpdatedDate,
            averagesValues.sourceLastUpdate,
            this.patientVisitsSourceUpdatedDate
          );

          this.defaultSource = source || SourceType.Contracted;
          sourceControl?.setValue(this.defaultSource);
          this.updateBanner(source);
        }

        // rollback enable state after updating form values
        this.setEnableStateForEditableControls(this.editMode$.getValue());

        this.initialFormValues = this.form.getRawValue();
      });
  }

  private fetchVendorList() {
    this.investigatorForecastService
      .getVendorListWithInvestigatorCosts()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.vendorListLoading$.next(true);
        })
      )
      .subscribe((list) => {
        this.vendorList = list.sort(({ name: label1 }, { name: label2 }) =>
          Utils.alphaNumSort(label1, label2)
        );

        const vendorIdFromStorage = this.investigatorForecastLsService.getLastSavedVendorByTrial(
          this.mainQuery.getValue().trialKey
        );

        if (vendorIdFromStorage) {
          this.form.get('vendorId')?.setValue(vendorIdFromStorage);
        } else if (this.vendorList.length) {
          this.form.get('vendorId')?.setValue(this.vendorList[0].id);
        }

        this.vendorListLoading$.next(false);
      });
  }

  private getConfirmInvestigatorForecastTitle(source: SourceType) {
    const mapTitle = new Map<SourceType, string>([
      [SourceType.Averages, this.sources[1].label],
      [SourceType.Contracted, this.sources[0].label],
      [SourceType.PatientVisits, this.sources[2].label],
    ]);

    return mapTitle.get(source) || '';
  }

  getLastSavedSource(
    contractedDate: null | string,
    averagesDate: null | string,
    patientVisitsDate: null | string
  ): SourceType {
    const savedDateList = [contractedDate, averagesDate, patientVisitsDate].filter(
      Boolean
    ) as string[];

    if (!savedDateList.length) {
      return SourceType.Contracted;
    }

    const latestDate = savedDateList.reduce((prevDate, currDate) => {
      const prevDayjs = dayjs(prevDate);
      const currDayjs = dayjs(currDate);

      return prevDayjs.isAfter(currDayjs) ? prevDate : currDate;
    });

    if (latestDate === contractedDate) {
      return SourceType.Contracted;
    }

    return latestDate === averagesDate ? SourceType.Averages : SourceType.PatientVisits;
  }

  navigateToBudgetForecast(vendor_id: string): void {
    void this.router.navigate([ROUTING_PATH.BUDGET.INDEX, ROUTING_PATH.BUDGET.INDEX], {
      queryParams: { scrollTo: 'remaining_unit_num', toggle: 'investigator', vendorId: vendor_id },
    });
  }

  private resetPageState() {
    this.form.setValue(
      {
        ...INITIAL_FORM_VALUES,
        ...this.initialFormValues,
      },
      { emitEvent: false }
    );

    this.assignBottomValuesToSourceOptions();
    this.editMode$.next(false);
  }

  saveSiteContractSetting = async () => {
    const responseModel = await this.showConfirmModal();

    if (!responseModel?.data?.result || !this.form.value.source) {
      return;
    }

    const saveMethod = this.mapSaveSiteContractBySource.get(this.form.value.source);

    if (saveMethod) {
      await saveMethod();
      const { vendorId } = this.form.value;

      if (!vendorId) {
        return;
      }

      this.investigatorForecastLsService.saveSettings(this.mainQuery.getValue().trialKey, vendorId);

      this.triggerFetchActualDataAfterSubmit$.next(true);
      this.editMode$.next(false);
    }
  };

  private setControlStateByCondition(control: AbstractControl, condition: boolean) {
    if (condition) {
      control.enable({ emitEvent: false, onlySelf: true });
    } else {
      control.disable({ emitEvent: false, onlySelf: true });
    }
  }

  private setEnableStateForEditableControls(condition: boolean) {
    this.editableControlNames.forEach((controlName) => {
      const control = this.form.get(controlName);

      if (control) {
        this.setControlStateByCondition(control, condition);
      }
    });
  }

  private showConfirmModal() {
    const { source } = this.form.value;

    if (!source) {
      return;
    }

    return firstValueFrom(
      this.overlayService.open<{ result: boolean }>({
        closeButton: false,
        content: InvestigatorForecastUpdateModalComponent,
        data: {
          source: this.getConfirmInvestigatorForecastTitle(source),
        },
      }).afterClosed$
    );
  }

  private async updateAverages() {
    const { vendorId } = this.form.value;

    if (vendorId) {
      const responses = await this.investigatorForecastService.saveSiteContractAverages(
        this.form.getRawValue()
      );

      const errors = flatMap(responses, (obj) => obj.errors);

      if (errors.length) {
        this.overlayService.error(errors);

        return;
      }

      this.overlayService.success('Investigator Forecast successfully updated!');
    }
  }

  private updateBanner(savedSource: null | SourceType) {
    if (!savedSource) {
      this.bannerValues$.next({
        amount: 0,
        source: '',
      });

      return;
    }

    const mapAmount = new Map<SourceType, number>([
      [SourceType.Averages, this.sources[1].forecastTotal || 0],
      [SourceType.Contracted, this.sources[0].forecastTotal || 0],
      [SourceType.PatientVisits, this.sources[2].forecastTotal || 0],
    ]);

    this.bannerValues$.next({
      amount: mapAmount.get(savedSource) || 0,
      source: this.getConfirmInvestigatorForecastTitle(savedSource),
    });
  }

  private async updatePatientVisits() {
    const formValues = this.form.getRawValue();

    if (!formValues.vendorId) {
      return;
    }

    const responses = await this.investigatorForecastService.savePatientVisits(formValues);

    const errors = flatMap(responses, (obj) => obj.errors);

    if (errors.length) {
      this.overlayService.error(errors);

      return;
    }

    this.overlayService.success('Investigator Forecast successfully updated!');
  }

  private updateSaveSettingAvailabilityState() {
    combineLatest([this.form.valueChanges, this.form.statusChanges])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(([{ source }]) => {
          if (source !== SourceType.Averages) {
            this.submitButtonDisabled$.next(false);

            return;
          }

          this.submitButtonDisabled$.next(this.form.invalid);
        })
      )
      .subscribe();
  }

  private async updateSiteContract() {
    const { contracted, vendorId } = this.form.getRawValue();

    if (!vendorId) {
      return;
    }

    const { errors } = await firstValueFrom(
      this.investigatorForecastService.updateInvestigatorForecast$(vendorId, {
        other_amount: contracted?.otherInvoiceables || 0,
        overhead_amount: contracted?.overheadInvoiceables || 0,
        patient_visits_amount: contracted?.totalVisitCosts || 0,
        source: InvestigatorForecastSource.CONTRACTED_AMOUNT,
      })
    );

    if (errors.length) {
      this.overlayService.error(errors);

      return;
    }

    this.overlayService.success('Investigator Forecast successfully updated!');
  }
}
